import { Injectable } from '@angular/core';
// import { OfficeComponentContext } from '@office-start/officehome-types';
// import { cleanUnusedThirdPartyWorkloads as cleanUserUnusedThirdPartyWorkloads, invalidateUsedThirdPartyAppCache } from '@office-start/workloads-service';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppTilesConfigService } from '../../app-tiles/shared/app-tiles-config.service';
import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { KnownReferralType } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { OfficeDotComOrigin } from '../models/telemetry-constants.model';
import { Workload } from '../models/workload.model';
// import { OfficeStartBootstrapperService } from '../office-start/office-start-bootstrapper.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { UrlBuilder } from '../utilities/url-builder.utility';
import { ConfigParserService } from './config-parser.service';
import { DateService } from './date.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { ShellShimService } from './shell-shim.service';
import { WorkloadsService } from './workloads.service';

export const enum FirstPartyWorkloadIds {
    Outlook = 'ShellMail',
    Word = 'ShellWordOnline',
    Excel = 'ShellExcelOnline',
    PowerPoint = 'ShellPowerPointOnline',
    OneNote = 'ShellOneNoteOnline',
    Teams = 'ShellSkypeTeams',
    SharePoint = 'ShellSites',
    Delve = 'ShellOfficeGraph',
    Video = 'ShellVideo',
    Project = 'ShellProject',
    Sway = 'ShellSway',
    Forms = 'ShellOfficeForms',
    ClassNotebook = 'ShellClassNotebook',
    Admin = 'ShellAdmin',
    Access = 'ShellAccess',
    Publisher = 'ShellPublisher',
    Visio = 'ShellVisioOnline',
    OneDrive = 'ShellDocuments',
    Calendar = 'ShellCalendar',
    Yammer = 'ShellYammer',
    Dynamics365 = 'ShellDynamics365',
    People = 'ShellPeople',
    Planner = 'ShellHighLander',
    Flow = 'ShellLogicFlows',
    PowerBIv2 = 'ShellPowerBIv2',
    PowerBI = 'ShellPowerBI',
    PowerApps = 'ShellPowerApps',
    Stream = 'ShellMicrosoftStream',
    DynamicsCRM = 'ShellCRM',
    DynamicsERP = 'ShellERP',
    DynamicsMarketing = 'ShellMarketing',
    Bookings = 'ShellBookings',
    BusinessCenter = 'ShellBusinessCenter',
    MyAnalytics = 'ShellMyAnalytics',
    Parature = 'ShellParature',
    SkypeMeetings = 'ShellSkypeMeetings',
    Skype = 'ShellSkype',
    DynamicsNAV = 'ShellDynamicsNAV',
    SocialEngagement = 'ShellNetbreeze',
    Newsfeed = 'ShellNewsfeed',
    OfficeStore = 'ShellOfficeStore',
    ComplianceCenter = 'ShellComplianceCenter',
    Kaizala = 'ShellKaizala',
    Tasks = 'ShellTasks',
    NonProfitPortal = 'ShellNonProfitPortal',
    ToDo = 'ShellToDo',
    StaffHub = 'ShellStaffHub',
    StaffNotebook = 'ShellStaffNotebook',
    Invoicing = 'ShellInvoicing',
    Lists = 'ShellLists',
    Whiteboard = 'ShellWhiteboard',
    FamilySafety = 'ShellFamilySafety',
    TeamsForLife = 'ShellTeams',
    Clipchamp = 'ShellClipchamp',
    Designer = 'ShellDesigner',
    Loop = 'ShellLoop',
    Defender = 'ShellDefender',
    Editor = 'ShellEditor'
}

/**
* Common interface in officehome to represent shell workloads data needed for app tiles.
*/
interface ShellWorkloadData {
    BackgroundColor: string;
    FontIconCss: string;
    LaunchUrl: string;
    Id: string;
}

interface ShellWorkloadsData {
    nonCoreAndSegmentApps: Array<Workload>;
    coreAndSegmentApps: Array<Workload>;
    pinnedApps: Array<Workload>;
}

/**
* Service that retrieves workload items as JSON.
*/
@Injectable()
export class OfficeWorkloadsService extends WorkloadsService {
    private static readonly AppTileElementIdPrefix = 'Shell';
    private static readonly AppTileElementIdPrefixLength = OfficeWorkloadsService.AppTileElementIdPrefix.length;
    private static readonly AppsShimDataSourceAppCollectors = 3;
    private static readonly AppsShimDataSourceShellDataComplete = 4;
    private readonly allWorkloads: ShellWorkloadsData;
    private static readonly idToFileTypeMap = new Map<string, FileType>([
        [FirstPartyWorkloadIds.Word, FileType.Word],
        [FirstPartyWorkloadIds.Excel, FileType.Excel],
        [FirstPartyWorkloadIds.PowerPoint, FileType.PowerPoint],
        [FirstPartyWorkloadIds.OneNote, FileType.OneNote],
        [FirstPartyWorkloadIds.ClassNotebook, FileType.OneNoteClassNoteBook],
        [FirstPartyWorkloadIds.Visio, FileType.Visio],
        [FirstPartyWorkloadIds.Sway, FileType.Sway],
        [FirstPartyWorkloadIds.Forms, FileType.Forms],
        [FirstPartyWorkloadIds.OneDrive, FileType.OneDrive],
        [FirstPartyWorkloadIds.SharePoint, FileType.SharePoint],
        [FirstPartyWorkloadIds.Stream, FileType.Stream]
    ]);
    private static readonly fileTypeToIdMap = new Map<FileType, string>([
        [FileType.Word, FirstPartyWorkloadIds.Word],
        [FileType.Excel, FirstPartyWorkloadIds.Excel],
        [FileType.PowerPoint, FirstPartyWorkloadIds.PowerPoint],
        [FileType.OneNote, FirstPartyWorkloadIds.OneNote],
        [FileType.Visio, FirstPartyWorkloadIds.Visio],
        [FileType.Sway, FirstPartyWorkloadIds.Sway],
        [FileType.Forms, FirstPartyWorkloadIds.Forms],
        [FileType.OneDrive, FirstPartyWorkloadIds.Sway],
        [FileType.SharePoint, FirstPartyWorkloadIds.Forms],
        [FileType.Stream, FirstPartyWorkloadIds.Stream]
    ]);

    protected nativeWorkloads: Array<Workload>;

    /**
     * Cached workloads data
     */
    private workloadsPromise: Promise<Array<Readonly<Workload>>>;
    private coreSegmentAndOtherAppsPromise: Promise<Array<Readonly<Workload>>>;
    private readonly thirdPartyWorkloads: Array<Readonly<Workload>>;
    private readonly thirdPartyWorkloadsSubject: BehaviorSubject<Array<Readonly<Workload>>>;
    private usedThirdPartyWorkloads: Array<Workload>;
    private readonly shellPinEnabled: boolean;
    // private readonly context: OfficeComponentContext;

    constructor(
        private readonly appTilesConfigService: AppTilesConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly configParserService: ConfigParserService,
        private readonly contextService: ContextService,
        private readonly dateService: DateService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        protected readonly startupConfigService: ClientStartupConfigService,
        private readonly scheduler: SchedulerService,
        private readonly shellShimService: ShellShimService
    ) {
        super();

        // if (this.capabilitiesService.capabilities.officeStartUsedThirdPartyAppsEnabled) {
        //     this.context = this.officeStartBootstrapperService.bootstrapOfficeStart();
        // }

        this.nativeWorkloads = new Array<Workload>();

        this.allWorkloads = this.configParserService.parse<ShellWorkloadsData>('workloadsData') || { nonCoreAndSegmentApps: [], coreAndSegmentApps: [], pinnedApps: [] };

        this.shellPinEnabled = this.capabilitiesService.capabilities.shellPinEnabled;

        // Populate extra fields for the pinned apps
        // TODO: consider doing this in the service after switching fully to using pinned apps.
        if (this.allWorkloads.pinnedApps) {
            this.populateWorkloadFields(this.allWorkloads.pinnedApps);
        }

        // filtering native\local apps.
        this.allWorkloads.coreAndSegmentApps = this.allWorkloads.coreAndSegmentApps.filter(workload => this.filterLocalOnlyApps(workload));

        this.usedThirdPartyWorkloads = [];
        this.thirdPartyWorkloads = [];
        this.thirdPartyWorkloadsSubject = new BehaviorSubject<Array<Workload>>([]);
        if (this.capabilitiesService.capabilities.officeStartUsedThirdPartyAppsEnabled) {
            // this.subscribeToThirdPartyAppUpdates().then(() => {
            //     const thirdPartyApps = this.thirdPartyWorkloads.map(app =>
            //         ({
            //             ...app,
            //             id: app.Id
            //         }));
            //     invalidateUsedThirdPartyAppCache(thirdPartyApps, this.context).then(() => cleanUserUnusedThirdPartyWorkloads(this.context));
            // });
        } else {
            this.subscribeToThirdPartyAppUpdates();
            this.getWorkloads().then(workloads => {
                this.usedThirdPartyWorkloads = workloads.filter(workload => workload.IsThirdParty);
                this.cleanUnusedThirdPartyWorkloads();
            });
        }
    }

    public getProvisionedWorkloads(): Promise<Array<Workload>> {
        return new Promise<Array<Workload>>((resolve, reject) => {
            this.getWorkloads()
                .then(workloads => {
                    const provisionedWorkloads = workloads.filter(workload => !!workload.Url);

                    // Add app id and collector id for first party apps so that pinning from gallery will work correctly.
                    provisionedWorkloads.forEach(provisionedWorkload => {
                        if (!provisionedWorkload.IsThirdParty) {
                            // Remove 'Shell' prefix from Id to form the AppId required by the Pin API on Shell Shim.
                            provisionedWorkload.AppId = provisionedWorkload.Id.slice(5);

                            // Hard code the Shell first party collector ID since that is all we deal with here.
                            provisionedWorkload.CollectorId = 'FirstParty';
                        }
                    });

                    resolve(provisionedWorkloads);
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.Dnsgi);
                    reject('Failed to get workloads list');
                });
        });
    }

    // Only to get new shell workloads with only frequent apps with clicks
    // angappak : This function appears to serve a dual purpose. It is primarily used to fetch filtered set of apps to display for consumer users from inside app tiles component.
    // However, in addition to that it is also used to determine the *number* of apps to display for commercial users. This is confusing and prone to break.
    // We should plan to refactor this in subsequent changes. Also, note that pinned apps are not currently in scope for consumer, so this remains unchanged.
    public getCoreSegmentAndFrequentApps(): Promise<Array<Workload>> {
        if (!this.coreSegmentAndOtherAppsPromise) {
            this.coreSegmentAndOtherAppsPromise = new Promise<Array<Workload>>(resolve => {
                const workloads = this.allWorkloads;
                const nonCoreAndSegmentApps = this.getFrequentAppsWithAnyClicks(workloads.nonCoreAndSegmentApps);
                const appsLinksAndWorkloads = workloads.coreAndSegmentApps.concat(nonCoreAndSegmentApps);
                this.populateWorkloadFields(appsLinksAndWorkloads);

                if (this.startupConfigService.config.authType === AuthType.MSA) {
                    this.getMsaWorkloadsData(appsLinksAndWorkloads).then(resolve);
                } else {
                    resolve(appsLinksAndWorkloads);
                }
            });
        }
        return this.coreSegmentAndOtherAppsPromise.then(workloads => [...workloads]);
    }

    // Get the pinned apps for commercial users only. Used for displaying pinned app tiles list.
    public getPinnedWorkloads(): Array<Workload> {
        return this.allWorkloads.pinnedApps;
    }

    public async unpinWorkload(workloadToUnpin: Workload) {
        // TODO: change this to an observable and pass it into app tiles list component to avoid storing a local copy there.
        this.allWorkloads.pinnedApps = this.allWorkloads.pinnedApps.filter(workload => !(workload.AppId === workloadToUnpin.AppId && workload.CollectorId === workloadToUnpin.CollectorId));

        const shellShim = await this.shellShimService.getShellShim();
        if (shellShim) {
            const appId = workloadToUnpin.AppId;
            const collectorId = workloadToUnpin.CollectorId;
            shellShim.Apps.Unpin(collectorId, appId);
        }
    }

    // Get all available workloads.
    // angappak : This is used to fetch list of all commercial workloads for a user.
    // This list is filtered down using the count returned from getCoreSegmentAndFrequentApps and the resulting filtered list
    // is used to provide the workloads bound to app tiles list for commercial users. This will need to completely replaced by getPinnedWorkloads eventually.
    // Note that this function is also used in several other locations to fetch full list of workloads. We might need to likely retain it for that purpose.
    public getWorkloads(): Promise<Array<Workload>> {
        if (!this.workloadsPromise) {
            this.workloadsPromise = new Promise<Array<Workload>>(resolve => {
                let nonCoreAndSegmentApps: Array<Workload>;
                let coreAndSegmentApps: Array<Workload>;
                let workloadsArray: Array<Workload>;

                const workloads = this.allWorkloads;
                nonCoreAndSegmentApps = workloads.nonCoreAndSegmentApps;
                coreAndSegmentApps = workloads.coreAndSegmentApps;
                workloadsArray = coreAndSegmentApps.concat(nonCoreAndSegmentApps);
                this.populateWorkloadFields(workloadsArray);

                if (this.startupConfigService.config.authType === AuthType.MSA) {
                    this.getMsaWorkloadsData(workloadsArray).then(resolve);
                } else {
                    resolve(workloadsArray);
                }
            });
        }
        return this.workloadsPromise.then(workloads => [...workloads]);
    }

    public getWorkloadId(fileType: FileType): string {
        return OfficeWorkloadsService.fileTypeToIdMap.has(fileType) ? OfficeWorkloadsService.fileTypeToIdMap.get(fileType) : '';
    }

    public getReferralType(workload: Workload): KnownReferralType {
        const clickWindowStart = this.dateService.addDays(this.dateService.getCurrentDate(), -45);
        if (!workload.ClickCount) {
            return KnownReferralType.FirsttimeReferral;
        } else if (workload.LastOccurrence < clickWindowStart.getTime()) {
            return KnownReferralType.ResurrectedReferral;
        } else if (workload.LastOccurrence >= clickWindowStart.getTime()) {
            return KnownReferralType.RepeatReferral;
        }

        return KnownReferralType.NotDetected;
    }

    public getDisplayedApps(workloads: Array<Workload>): string {
        const appTileConfig = this.appTilesConfigService.config;
        const coreAndSegmentAppIds = (!appTileConfig.coreAppIds || !appTileConfig.segmentSpecificAppIds) ? (appTileConfig.coreAppIds || appTileConfig.segmentSpecificAppIds) : appTileConfig.coreAppIds.concat(appTileConfig.segmentSpecificAppIds);
        const displayedApps = new Array<string>();

        // Add apps displayed on homepage to the list,  include 9 core apps, segment apps and frequent apps
        // The apps are in a descending order based on click counts
        // Same logic as in app-tiles-list.component.ts
        if (coreAndSegmentAppIds && coreAndSegmentAppIds.length) {
            const coreAndSegmentApps = workloads.filter(app => (coreAndSegmentAppIds.indexOf(app.Id) > -1));
            const nonCoreAndSegmentApps = workloads.filter(app => (coreAndSegmentAppIds.indexOf(app.Id) < 0));
            const coreApps = appTileConfig.coreAppIds ? workloads.filter(app => appTileConfig.coreAppIds.indexOf(app.Id) > -1) : [];

            // Show all apps if
            //     * User doesn't have any core and segment apps or
            //     * User doesn't have any non core/segment apps or
            //     * User has 0-2 core apps
            if (!coreAndSegmentApps.length || !nonCoreAndSegmentApps.length || coreApps.length < 3) {
                for (const app of workloads) {
                    displayedApps.push(app.Id);
                }
            } else {
                const displayedFrequentApps = this.getFrequentAppsWithAnyClicks(nonCoreAndSegmentApps);
                for (const app of coreAndSegmentApps) {
                    displayedApps.push(app.Id);
                }
                if (displayedFrequentApps.length) {
                    for (const app of displayedFrequentApps) {
                        displayedApps.push(app.Id);
                    }
                }
            }

            return displayedApps.toString();
        }
    }

    // Returns frequent apps with clicks
    public getFrequentAppsWithAnyClicks(apps: Array<Workload>): Array<Workload> {
        // will not show an app in frequent apps section if they have zero click count or the last click was outside the 45 day window
        const clickWindowStart = this.dateService.addDays(this.dateService.getCurrentDate(), -45);
        const frequentAppsFilter = (workload: Workload) => workload.ClickCount > 0 && workload.LastOccurrence >= clickWindowStart.getTime();
        return apps.filter(frequentAppsFilter);
    }

    public getThirdPartyWorkloads(): Observable<Array<Workload>> {
        return this.thirdPartyWorkloadsSubject.asObservable();
    }

    public updateUsedThirdPartyApps(app: Workload) {
        // Commented out until we get further instructions from CELA on guid appId in CosmosDB
        if (this.capabilitiesService.capabilities.officeStartUsedThirdPartyAppsEnabled) {
            // const data = {
            //     ...app,
            //     id: app.Id
            // };
            // updateUsedThirdPartyAppsUserData(data, this.context);
        } else {
            const cachedWorkloadIndex = this.usedThirdPartyWorkloads.findIndex(workload => workload.Id.toLowerCase() === app.Id.toLowerCase());
            const cachedWorkload = cachedWorkloadIndex >= 0 ? this.usedThirdPartyWorkloads[cachedWorkloadIndex] : undefined;

            if (cachedWorkload) {
                if (!this.areThirdPartyAppsEqual(cachedWorkload, app)) {
                    this.usedThirdPartyWorkloads[cachedWorkloadIndex] = app;
                    // Commented out until we get further instructions from CELA on guid appId in CosmosDB
                    // this.userDataStorageService.set<Array<Workload>>(UserStorageKeys.UsedThirdPartyApps, this.usedThirdPartyWorkloads);
                }
            } else {
                this.usedThirdPartyWorkloads.push(app);
                // Commented out until we get further instructions from CELA on guid appId in CosmosDB
                // this.userDataStorageService.set<Array<Workload>>(UserStorageKeys.UsedThirdPartyApps, this.usedThirdPartyWorkloads);
            }
        }
    }

    public populateWorkloadFields(workloads: Array<Workload>) {
        workloads.forEach(workload => {
            // Workload icon css is maintained and returned from service, so no overrides are needed here.
            workload.getId = () => workload.Id;
            workload.Referral = workload.IsThirdParty ? workload.Id : OfficeWorkloadsService.referralFromWorkloadId(workload.Id);
            workload.FileType = this.getFileType(workload.Id);
            workload.Url = this.getWorkloadCustomUrl(workload.FileType, workload.Url);
        });
    }

    public getAdditionalWorkloads(): Promise<Array<Workload>> {
        return Promise.resolve([]);
    }

    public pinWorkload(workload: Workload): void {
        if (this.shellPinEnabled) {
            this.shellShimService.getShellShim()
                .then(shellShim => {
                    const appId = workload.AppId;
                    const collectorId = workload.CollectorId;
                    if (collectorId && appId && !shellShim.Apps.IsPinned(collectorId, appId)) {
                        shellShim.Apps.Pin(collectorId, appId);
                    }
                });
        }
    }

    public getInAppNav(workloadId: string): boolean {
        const workloads = this.allWorkloads.coreAndSegmentApps.concat(this.allWorkloads.nonCoreAndSegmentApps).concat(this.allWorkloads.pinnedApps);
        const workload = workloads.find(app => app.Id === workloadId);

        return workload ? workload.InAppNav : false;
    }

    public getFileType(workloadId): FileType {
        return OfficeWorkloadsService.idToFileTypeMap.has(workloadId) ? OfficeWorkloadsService.idToFileTypeMap.get(workloadId) : FileType.Unknown;
    }

    protected getMsaWorkloadsData(currentWorkloads: Array<Workload>): Promise<Array<Workload>> {
        return this.getShellWorkloadsData().then((shellWorkloadsData: Array<ShellWorkloadData>) => {
            this.populateMSAWorkloadFields(currentWorkloads, shellWorkloadsData);
            return currentWorkloads;
        });
    }

    private static referralFromWorkloadId(id: string): string {
        return id && id.length && id.lastIndexOf(OfficeWorkloadsService.AppTileElementIdPrefix, OfficeWorkloadsService.AppTileElementIdPrefixLength) === 0
            ? id.substring(OfficeWorkloadsService.AppTileElementIdPrefixLength)
            : null;
    }

    private filterLocalOnlyApps(workload: Workload): boolean {
        if (!workload.IsNativeClientOnlyApp) {
            return true;
        }
        this.nativeWorkloads.push(workload);
        return false;
    }

    private populateMSAWorkloadFields(workloads: Array<Workload>, shellWorkloadsData: Array<ShellWorkloadData>) {
        workloads.forEach(workload => {
            const workloadId = workload.Id.toLowerCase();
            for (let i = 0; i < shellWorkloadsData.length; i++) {
                const shellWorkloadLink = shellWorkloadsData[i];
                if (shellWorkloadLink.Id.toLowerCase() === workloadId) {
                    // Workload icon css is maintained and returned from service, so no overrides are needed here.
                    workload.Color = shellWorkloadLink.BackgroundColor;
                    workload.FileType = this.getFileType(workload.Id);
                    // don't update workload URL if it has none -- this means tile is provisioning
                    if (workload.Url) {
                        workload.Url = this.getWorkloadCustomUrl(workload.FileType, shellWorkloadLink.LaunchUrl);
                    }
                    break;
                }
            }
        });
    }

    /**
     * Adds additional parameters to the workload url.
     * -Forms:
     *      origin: forms.office.com requires an origin parameter to collect telemetry and determine if traffic is from office.com or shell
     *      lang: forms.office.com requires lang parameter to dictate which language to use on the forms.office.com redirected website because they obtain their language settings from browser and not from the shell service.
     * @param workloadType
     * @param url
     */
    private getWorkloadCustomUrl(workloadType: FileType, url: string): string {
        switch (workloadType) {
            case FileType.Forms:
                url = UrlBuilder.appendAndReplaceQueryParams(url, {
                    origin: OfficeDotComOrigin,
                    lang: this.contextService.sharedContext.officeMarket,
                    sessionId: this.startupConfigService.config.sharedClientStartupContext.sessionId
                }, false, true);
                break;
        }
        return url;
    }

    private getShellWorkloadsData(): Promise<Array<ShellWorkloadData>> {
        return new Promise<Array<ShellWorkloadData>>(async (resolve, reject) => {
            // Fetch shell workloads data using the callback defined by react shell.
            const shellShim = await this.shellShimService.getShellShim();
            if (shellShim) {
                shellShim.Apps.OnAppsUpdated(evt => {
                    if (evt && evt.Apps) {
                        const shellWorkloads = evt.Apps.map(item => ({
                            BackgroundColor: item.BackgroundColor,
                            FontIconCss: item.FontIconCss,
                            Id: item.Id,
                            LaunchUrl: item.LaunchFullUrl
                        } as ShellWorkloadData));
                        resolve(shellWorkloads);
                    } else {
                        reject('Shell shim apps not available.');
                    }
                });
            } else {
                reject('Shell shim is not available');
            }
        });
    }

    private async subscribeToThirdPartyAppUpdates() {
        const shellShim = await this.shellShimService.getShellShim();
        await this.shellShimService.afterShellPlusLoaded;

        const allIconsFetched = new Promise<any>(resolve => {
            let iconFetchesStarted = 0;
            let iconFetchesCompleted = 0;
            let appFetchComplete = false;

            shellShim.Apps.OnAppsUpdated(evt => {
                if (evt && evt.Apps) {
                    let nonFirstPartyApps = evt.Apps.filter(app => app.CollectorId !== 'FirstParty'
                        && !this.thirdPartyWorkloads.find(workload => workload.Id === app.Id));

                    nonFirstPartyApps = nonFirstPartyApps.filter(app => app.CollectorId !== 'LineOfBusiness');

                    // Do not filter Dynamics apps when BusinessApps flight is enabled.
                    if (!this.capabilitiesService.capabilities.showBusinessApps) {
                        nonFirstPartyApps = nonFirstPartyApps.filter(app => app.CollectorId !== 'Dynamics');
                    }

                    nonFirstPartyApps.forEach(app => {
                        const url = app.LaunchFullUrl;
                        this.thirdPartyWorkloads.push({
                            ChangeColorInDarkMode: app.CollectorId === 'Dynamics',
                            Color: app.BackgroundColor,
                            Id: app.Id,
                            AppId: app.Id,
                            CollectorId: app.CollectorId,
                            Title: app.AriaLabel ? app.AriaLabel : '',
                            SubText: app.Subtitle,
                            Text: app.Title,
                            // TODO: Remove 'as any' typing by updating shell typings
                            IsNativeClientOnlyApp: (app as any).isNativeClientOnlyApp,
                            Url: !!url ? url.trim() : '',
                            IconUnicodeCodepointCssClass: 'ms-fcl-tp ms-Icon ms-Icon--OfficeAddinsLogo',
                            ThirdPartyIcon: app.IconAnonymousUrl ? app.IconAnonymousUrl : null,
                            Referral: app.Id,
                            IsThirdParty: true,
                            getId: () => app.Id
                        } as Workload);

                        if (!app.IconAnonymousUrl) {
                            ++iconFetchesStarted;

                            this.scheduler.scheduleWork(() => {
                                shellShim.Apps.GetAuthenticatedAppIcon(app, appIcon => {
                                    ++iconFetchesCompleted;

                                    if (appIcon) {
                                        const workloadIndex = this.thirdPartyWorkloads.findIndex(thirdPartyWorkload => thirdPartyWorkload.Id.toLowerCase() === app.Id.toLowerCase());
                                        const workload = this.thirdPartyWorkloads[workloadIndex];

                                        this.thirdPartyWorkloads[workloadIndex] = { ...workload, ...{ ThirdPartyIcon: `data:${appIcon.MimeType};base64,${appIcon.Base64EncodedImage}` } };
                                        this.thirdPartyWorkloadsSubject.next(this.thirdPartyWorkloads);
                                    }

                                    if (appFetchComplete && iconFetchesStarted === iconFetchesCompleted) {
                                        resolve();
                                    }
                                });
                            }, SchedulePriority.Low);
                        }
                    });

                    this.thirdPartyWorkloadsSubject.next(this.thirdPartyWorkloads);

                    appFetchComplete = evt.DataSource === OfficeWorkloadsService.AppsShimDataSourceAppCollectors
                        || evt.DataSource === OfficeWorkloadsService.AppsShimDataSourceShellDataComplete;

                    if (appFetchComplete && iconFetchesStarted === iconFetchesCompleted) {
                        resolve();
                    }
                }
            });
        });

        await allIconsFetched;

        if (!this.capabilitiesService.capabilities.officeStartUsedThirdPartyAppsEnabled) {
            this.invalidateThirdPartyAppCache();
        }
    }

    private cleanUnusedThirdPartyWorkloads() {
        if (this.usedThirdPartyWorkloads.length === 0) {
            return;
        }

        const clickWindowStart = this.dateService.addDays(this.dateService.getCurrentDate(), -45).getTime();
        const oldWorkloadsLength = this.usedThirdPartyWorkloads.length;

        this.usedThirdPartyWorkloads = this.usedThirdPartyWorkloads.filter(workload => workload.LastOccurrence >= clickWindowStart);

        if (oldWorkloadsLength !== this.usedThirdPartyWorkloads.length) {
            // Commented out until we get further instructions from CELA on guid appId in CosmosDB
            // this.userDataStorageService.set<Array<Workload>>(UserStorageKeys.UsedThirdPartyApps, this.usedThirdPartyWorkloads);
        }
    }

    private areThirdPartyAppsEqual(workload1: Workload, workload2: Workload) {
        return workload1.Text === workload2.Text
            && workload1.Url === workload2.Url
            && workload1.ThirdPartyIcon === workload2.ThirdPartyIcon
            && workload1.Title === workload2.Title;
    }

    private invalidateThirdPartyAppCache() {
        if (this.usedThirdPartyWorkloads.length === 0) {
            return;
        }

        const updatedUsedThirdPartyWorkloads = [];
        let listUpdated = false;

        this.usedThirdPartyWorkloads.forEach(usedThirdPartyWorkload => {
            const workload = this.thirdPartyWorkloads.find(thirdPartyWorkload => thirdPartyWorkload.Id === usedThirdPartyWorkload.Id);

            if (!workload) {
                listUpdated = true;
            } else if (!this.areThirdPartyAppsEqual(workload, usedThirdPartyWorkload)) {
                updatedUsedThirdPartyWorkloads.push(workload);
                listUpdated = true;
            } else {
                updatedUsedThirdPartyWorkloads.push(workload);
            }
        });

        if (listUpdated) {
            this.usedThirdPartyWorkloads = updatedUsedThirdPartyWorkloads;
            // Commented out until we get further instructions from CELA on guid appId in CosmosDB
            // this.userDataStorageService.set<Array<Workload>>(UserStorageKeys.UsedThirdPartyApps, this.usedThirdPartyWorkloads);
        }
    }
}
